import { DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { SquareCardComponent } from '@app/components/payments/square-card/square-card.component';
import { GenericDialogComponent } from '@app/management/dialogs/generic-confirm/generic-confirm.component';
import { Clinic } from '@models/clinic';
import { ErrorMessageType } from '@models/error-message-type.enum';
import { ConvergePayment, ConvergePaymentStatus, ConvergePaymentType } from '@models/finance/converge-payment';
import { FinanceTransactionType } from '@models/finance/finance-transaction-type';
import { InvoicePayment } from '@models/finance/invoice-payment';
import { InvoiceTransaction } from '@models/finance/invoice-transaction';
import { PaymentMethod } from '@models/finance/payment-method';
import { PaymentMethodEnum } from '@models/finance/payment-method.enum';
import { GiftCard } from '@models/gift-cards/gift-card';
import { Invoice } from '@models/invoice/invoice';
import { InvoiceLineItem } from '@models/invoice/invoice-line-item';
import { InvoiceType } from '@models/invoice/invoice-type';
import { PaidCancellation } from '@models/invoice/paid-cancellation';
import { Patient } from '@models/patient';
import { ConvergeCard } from '@models/payments/converge-card';
import { PaymentProcessorType } from '@models/payments/payment-processor-type.enum';
import { ResultModel } from '@models/result-model';
import { SquareCard } from '@models/square/square-card';
import { SquarePayment, SquarePaymentStatus, SquarePaymentType } from '@models/square/square-payment';
import { AppointmentService } from '@services/appointments.service';
import { BlobService } from '@services/blob.service';
import { ClinicsService } from '@services/clinics.service';
import { ConvergePaymentsService } from '@services/converge-payments.service';
import { FinanceService } from '@services/finance.service';
import { PaymentHelper } from '@services/helpers/payment-helper';
import { InvoicesService } from '@services/invoices.service';
import { MerchantDeviceService } from '@services/merchant-device.service';
import { PatientService } from '@services/patient.service';
import { PaymentMethodsService } from '@services/payment-methods.service';
import { SquareService } from '@services/square.service';
import { UsersService } from '@services/users.service';
import { BehaviorSubject, Observable, ReplaySubject, Subject, combineLatest, defer } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { StandardInvoiceComponent } from '../standard-invoice/standard-invoice.component';
import { PaidCancellationInvoiceTransaction } from './../../../../models/finance/paid-cancellation-invoice-transaction';

export enum PaymentType {
  OverPayment,
  Regular,
  Recurring,
  RefundProduct,
  RefundService,
  RefundAdminFee,
  RefundPaidCancellation,
  RefundCredit,
}

@Component({
  selector: 'app-invoice-payment',
  templateUrl: './invoice-payment.component.html',
  styleUrls: ['./invoice-payment.component.less'],
})
export class InvoicePaymentComponent implements OnInit, OnDestroy, OnChanges {
  get isSquareLinked() {
    return this.clinicsService.isSquareLinked;
  }

  get clinic(): Clinic {
    return this.clinicsService.clinic;
  }

  get financeTransactionTypes(): typeof FinanceTransactionType {
    return FinanceTransactionType;
  }

  private _patientId: number;
  @Input() set patientId(patientId: number) {
    this._patientId = patientId;
    this.setPatient();
  }
  get patientId() {
    return this._patientId;
  }

  private _paidCancellation: PaidCancellation;
  @Input() set paidCancellation(paidCancellation: PaidCancellation) {
    this._paidCancellation = paidCancellation;
    this.setPaidCancellation();
  }
  get paidCancellation() {
    return this._paidCancellation;
  }

  @Input() set invoice(invoice: Invoice) {
    this.invoice$.next(invoice);
  }
  get invoice(): Invoice {
    return this.invoice$.value;
  }

  @Input() parentClosed;
  @Input() editTransactionMode = false;

  @Output() paymentComplete = new EventEmitter();
  @Output() backToReturnPanel = new EventEmitter();

  @ViewChild('exportTemplate', { read: ViewContainerRef, static: true }) exportTemplate: ViewContainerRef;
  @ViewChild(SquareCardComponent, { static: false }) squareCardComponent: SquareCardComponent;

  patient: Patient;
  paymentForm: FormGroup;
  formPaymentMethods: FormArray;
  paymentMethods: PaymentMethod[];
  merchantDevicePaymentMethods: PaymentMethod[];
  creditsPaymentMethod: PaymentMethod;
  giftCardPaymentMethod: PaymentMethod;
  patientAvailableCredit = 0;
  patientAvailableLoyaltyPoints: { value: number; dollarValue: number } = { value: 0, dollarValue: 0 };
  outstandingAmount: number;
  initialOutstandingAmount: number;
  paidAmount: number;
  errors: any[] = [];
  warnings: any[] = [];
  invoiceLineItemTaxKeys: string[];
  invoiceLineItemTaxValuesObject: any;
  unsub = new Subject<any>();
  isGeneratedPDF = false;
  loading = false;
  showCloseInvoiceButton = false;
  adminFee: number = 0;
  stateOfCredits = 0;
  creditFieldExists = false;
  isAdminFeePaid = false;
  paymentType: PaymentType;

  PaymentType = PaymentType;
  PaymentHelper = PaymentHelper;
  PaymentProcessorType = PaymentProcessorType;
  ErrorMessageType = ErrorMessageType;

  // Converge payment
  deviceCapablePaymentMethods: PaymentMethod[];
  refundSquareCard: SquareCard;
  refundConvergeCard: ConvergeCard;
  refundConvergeFinanceTransaction: InvoiceTransaction;

  // Square payment
  selectedSquareCard: SquareCard = null;
  selectedConvergeCard: ConvergeCard = null;
  patientTokenId: number;
  squarePaymentMethod: PaymentMethod;
  refundCharged: boolean = false;
  membershipCharged: boolean = false;

  // Emits whenever the invoice Input changes
  private invoice$ = new BehaviorSubject<Invoice>(null);

  // Emits once when payment methods are fully loaded
  private paymentMethodsLoaded$ = new ReplaySubject<void>(1);

  constructor(
    private formBuilder: FormBuilder,
    private financeService: FinanceService,
    private paymentMethodsService: PaymentMethodsService,
    private route: ActivatedRoute,
    private patientService: PatientService,
    private invoicesService: InvoicesService,
    private decimalPipe: DecimalPipe,
    private appointmentService: AppointmentService,
    private invoiceService: InvoicesService,
    private usersService: UsersService,
    private clinicsService: ClinicsService,
    private blobService: BlobService,
    private merchantDeviceService: MerchantDeviceService,
    private squareService: SquareService,
    private dialog: MatDialog,
    private convergePaymentsService: ConvergePaymentsService
  ) {}

  ngOnInit() {
    this.getPaymentMethods();
    this.detectInvoiceUpdates();
    this.getCurrentPatientId();
    this.getPatientAvailableCredit();
    this.getPatientAvailableLoyaltyPoints();
    this.getPatientAvailableLoyaltyPointsDollarValue();
    this.initForm();

    // Only once *both* invoice$ and paymentMethodsLoaded$ have emitted, call getInvoiceDetails
    combineLatest([
      this.invoice$.pipe(filter((invoice) => !!invoice)), // wait for a non-null invoice
      this.paymentMethodsLoaded$,
    ])
      .pipe(takeUntil(this.unsub))
      .subscribe(([invoice]) => {
        this.getInvoiceDetails();
      });
  }

  ngOnChanges(changes: { [property: string]: SimpleChange }) {
    // If the parent component is closed, clear the errors
    let change: SimpleChange = changes['parentClosed'];
    if (change?.currentValue == true) {
      this.errors = [];
      this.onExit();
    }
  }

  private setPatient() {
    if (this.patientId) {
      this.patientService.getPatientById(this.patientId).subscribe((patient) => {
        if (patient) {
          this.patient = patient;
          this.paymentForm.get('emailAddress')?.setValue(patient.email);
        }
      });
    } else {
      this.patient = null;
    }
  }

  private setPaidCancellation() {
    if (this.paidCancellation) {
      this.loading = true;
      this.financeService
        .getFinanceTransaction(this.paidCancellation.financeTransactionId)
        .subscribe((financeTransaction) => {
          if (financeTransaction) {
            if (financeTransaction.squarePayment?.squareCardId) {
              this.squareService.getCardById(financeTransaction.squarePayment.squareCardId).subscribe((card) => {
                this.refundSquareCard = card;
                this.loading = false;
              });
            }
            if (financeTransaction.convergePayment?.patientTokenId) {
              this.convergePaymentsService
                .getPatientCardDetails(financeTransaction.convergePayment.patientTokenId)
                .subscribe((convergeCard) => {
                  this.refundConvergeCard = convergeCard;
                  this.refundConvergeFinanceTransaction = financeTransaction;
                  this.loading = false;
                });
            }
          } else {
            this.refundSquareCard = null;
            this.refundConvergeCard = null;
            this.refundConvergeFinanceTransaction = null;
            this.loading = false;
          }
        });
    }
  }

  initForm() {
    this.paymentForm = this.formBuilder.group(
      {
        formPaymentMethods: this.formBuilder.array([]),
        emailAddress: this.patient?.email,
        addBackToInventory: false,
        adminFeeToggle: false,
        adminFee: 0,
      },
      {
        validators: [this.paymentValidator],
      }
    );

    this.initFormChangeSubscriptions();
    this.resetAdminFeeControls();
  }

  private initFormChangeSubscriptions() {
    this.paymentForm
      .get('addBackToInventory')
      .valueChanges.pipe(takeUntil(this.unsub))
      .subscribe((val) => {
        if (val !== undefined && val !== null) {
          this.invoice.invoiceLineItems.forEach((item) => {
            if (item.clinicProductId) item.addBackToInventory = val;
          });
        }
      });
  }

  paymentValidator(formGroup: FormGroup) {
    if (formGroup.controls.formPaymentMethods && formGroup.controls.formPaymentMethods.status == 'VALID') {
      return null;
    }
    return { invalid: true };
  }

  isPaymentFormValid() {
    return this.paymentForm.valid;
  }

  detectInvoiceUpdates() {
    this.invoicesService.invoicesListUpdated$.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.getPatientAvailableLoyaltyPoints();
      this.getPatientAvailableLoyaltyPointsDollarValue();
      this.getPatientAvailableCredit();
    });
    this.financeService.invoicePaid$.pipe(takeUntil(this.unsub)).subscribe(() => {
      this.getPatientAvailableLoyaltyPoints();
      this.getPatientAvailableLoyaltyPointsDollarValue();
      this.getPatientAvailableCredit();
    });
  }

  createPaymentMethodRow(
    id?: number,
    transactionDate?: Date,
    createdById?: string,
    method?: PaymentMethod,
    amount?: number,
    isMerchantDeviceTransaction: boolean = false,
    isManualEntry: boolean = false,
    merchantDeviceId: number = 0,
    isCharged: boolean = false
  ): FormGroup {
    return this.formBuilder.group({
      id: id ? id : 0,
      transactionDate: transactionDate
        ? transactionDate
        : this.invoice && this.invoice.invoiceTransactions?.length != 0
        ? this.invoice.invoiceDate
        : new Date(),
      createdById: createdById ? createdById : this.usersService.loggedInUser.id,
      method: new FormControl(
        method ? { value: method, disabled: isMerchantDeviceTransaction } : '',
        Validators.required
      ),
      amount: new FormControl(
        amount != null
          ? { value: this.formatCurrency(amount), disabled: isMerchantDeviceTransaction }
          : { value: '', disabled: isMerchantDeviceTransaction },
        {
          validators: Validators.required,
          updateOn: 'blur',
        }
      ),
      isMerchantDeviceTransaction: isMerchantDeviceTransaction ? isMerchantDeviceTransaction : false,
      isManualEntry: new FormControl({ value: isManualEntry, disabled: this.editTransactionMode }),
      merchantDeviceId: merchantDeviceId ? merchantDeviceId : 0,
      isCharged: isCharged,
      giftCard: new FormControl(null),
    });
  }

  formatCurrencyForControl(unformattedCurrency: string | number, paymentMethod: FormGroup | FormControl) {
    try {
      let formattedCurrency = this.formatCurrency(unformattedCurrency);
      paymentMethod.get('amount').setValue(formattedCurrency, { emitEvent: false });
    } catch (e) {}
  }

  formatCurrency(unformattedCurrency: string | number) {
    let formattedCurrency = this.decimalPipe
      .transform(String(unformattedCurrency).replace(',', ''), '1.2-2')
      .replace(',', '');
    return formattedCurrency;
  }

  addToPaymentTotal(amount: number) {
    // Ensure that the outstanding amount is a positive number
    if (!this.editTransactionMode && amount < 0) {
      amount = amount * -1;
    }
    let pcs: Array<FormControl>;
    pcs = this.paymentForm.controls.formPaymentMethods['controls'];

    if (pcs.length > 0) {
      const found = pcs.some((pc) => {
        if (!pc.get('amount').value) {
          this.formatCurrencyForControl(amount, pc);
          this.outstandingAmount -= amount;
          if (pc.get('method').value?.id == this.creditsPaymentMethod.id) {
            this.stateOfCredits -= amount;
          }
          return true;
        }
        return false;
      });
      if (!found) {
        this.addPaymentMethodRow();
        this.addToPaymentTotal(amount);
      }
    } else {
      this.addPaymentMethodRow();
      this.addToPaymentTotal(amount);
    }
  }

  addPaymentMethodRow() {
    this.formPaymentMethods = this.paymentForm.get('formPaymentMethods') as FormArray;
    this.formPaymentMethods.push(this.createPaymentMethodRow());
  }

  removePaymentMethodRow(index: number) {
    //if credit is removed give back
    if (this.formPaymentMethods?.value[index]?.method?.id == this.creditsPaymentMethod?.id) {
      this.stateOfCredits += Number(this.formPaymentMethods?.value[index]?.amount);
      this.creditFieldExists = false;
    }

    this.outstandingAmount += Number(this.formPaymentMethods?.value[index]?.amount);
    this.formPaymentMethods = this.paymentForm.get('formPaymentMethods') as FormArray;
    this.formPaymentMethods?.removeAt(index);
  }

  adminFeeChanged(event) {
    let previousAdminFee = this.adminFee;
    this.adminFee = event && event.target && event.target.value ? +event.target.value.replace('$', '') : 0;
    this.outstandingAmount =
      this.paymentType === PaymentType.Regular || this.paymentType === PaymentType.Recurring
        ? this.outstandingAmount + this.adminFee - previousAdminFee
        : this.outstandingAmount - this.adminFee + previousAdminFee;
  }

  onFormChange(newItem, i, typeChange) {
    // If PaymentMethod Type changes, determine if we weill show Manual entry or not
    if (typeChange) {
      if (this.clinic?.isUsingMerchantDevices) {
        if (newItem.isMerchantDeviceTransaction) {
          var merchantDevicePaymentMethod = this.merchantDevicePaymentMethods.find((p) => p.id == newItem.id);
          if (merchantDevicePaymentMethod) {
            this.paymentForm.controls.formPaymentMethods['controls'][i]
              .get('isMerchantDeviceTransaction')
              .setValue(true, { emitEvent: false });
          } else {
            this.paymentForm.controls.formPaymentMethods['controls'][i]
              .get('isMerchantDeviceTransaction')
              .setValue(false, { emitEvent: false });
          }
        } else {
          this.paymentForm.controls.formPaymentMethods['controls'][i]
            .get('isMerchantDeviceTransaction')
            .setValue(false, { emitEvent: false });
        }
      }
    }

    var oldValues = this.paymentForm.controls.formPaymentMethods['controls'];

    var diff = 0;
    //Get the difference between old and new value when changing amount manually
    if (newItem != undefined && i != undefined && !typeChange) {
      diff = (Number(newItem) * 100 - Number(oldValues[i].value.amount) * 100) / 100;
    }

    //Modify outstanding amount with difference in value
    if (!typeChange) {
      this.outstandingAmount = Math.round(this.outstandingAmount * 100 - diff * 100) / 100;
    }

    //Handle amount credit changes
    if (this.creditFieldExists && !typeChange && oldValues[i].value.method?.id == this.creditsPaymentMethod.id) {
      var diff = Number(newItem) - Number(oldValues[i].value.amount);
      //GIVE BACK OR DEDUCT MORE CREDIT
      this.stateOfCredits -= diff;
    }

    //Determine if credit line added or removed by changing type (not deleting)
    if (typeChange) {
      if (newItem.id == this.creditsPaymentMethod.id && oldValues[i].value.method?.id != this.creditsPaymentMethod.id) {
        this.creditFieldExists = true;
        //Use credits
        this.stateOfCredits -= Number(oldValues[i].value.amount);
      }
      if (newItem.id != this.creditsPaymentMethod.id && oldValues[i].value.method?.id == this.creditsPaymentMethod.id) {
        this.creditFieldExists = false;
        // GIVE BACK FULL CREDIT
        this.stateOfCredits += Number(oldValues[i].value.amount);
      }
    }
  }

  onGiftCardSelected(giftCard: GiftCard, rowGroup: FormGroup) {
    if (giftCard) {
      const cardControl = rowGroup.get('giftCard');
      cardControl.setValue(giftCard);
    }
  }

  isMerchantDevice(paymentMethodControls: any): any {
    return paymentMethodControls.controls.isMerchantDeviceTransaction.value;
  }

  toggleManualEntry(paymentMethodControls: FormGroup | FormControl) {
    let currentValue = paymentMethodControls.get('isManualEntry').value;
    let newValue = !currentValue;
    paymentMethodControls.get('isManualEntry').setValue(newValue, { emitEvent: false });
  }

  isCharged(paymentMethodControls: FormGroup | FormControl) {
    let isCharged = paymentMethodControls.get('isCharged').value;
    return isCharged;
  }

  isPayButtonDisabled(paymentRow: FormGroup) {
    const paymentMethod = paymentRow.get('method').value as PaymentMethod;
    const amount = Number(paymentRow.get('amount').value);
    if (!amount || !paymentMethod) return true;
    const giftCard = paymentRow.get('giftCard')?.value as GiftCard;
    if (
      paymentMethod.id === this.giftCardPaymentMethod.id &&
      (giftCard == null || giftCard.balance == null || giftCard.balance < amount)
    )
      return true;
    return this.checkInvalidPaymentAmount();
  }

  checkForDifferences(a, b) {
    return a.filter((el) => {
      return !b.includes(el);
    });
  }

  getCurrentPatientId() {
    if (!this.patientId) {
      this.patientId = this.route.snapshot.params.patId.split('_')[0];
    }
  }

  async getInvoiceDetails() {
    if (this.invoice == null) return;
    this.loading = true;
    this.setModalPaymentType();
    this.getInvoiceLineItemTaxes(this.invoice);
    this.getInvoicePaidAmount(this.invoice.id);
    this.selectedSquareCard = null;
    this.creditFieldExists = false;
    this.stateOfCredits = 0;

    const outstandingAmount = await this.financeService.getOutstandingAmount(this.invoice.id).toPromise();

    if (
      !this.invoice.isPaid &&
      this.invoice.invoiceTransactions &&
      this.invoice.invoiceTransactions.length > 0 &&
      this.editTransactionMode == true
    ) {
      this.paymentForm.removeControl('formPaymentMethods');
      this.formPaymentMethods = this.formBuilder.array([], Validators.required);
      this.paymentForm.addControl('formPaymentMethods', this.formPaymentMethods);
      this.formPaymentMethods = this.paymentForm.get('formPaymentMethods') as FormArray;
      this.invoice.invoiceTransactions.forEach((transaction) => {
        var paymentMethod = null;
        if (transaction.isMerchantDeviceTransaction)
          paymentMethod = this.deviceCapablePaymentMethods.find((p) => p.id == transaction.paymentMethodId);
        else paymentMethod = this.paymentMethods.find((p) => p.id == transaction.paymentMethodId);

        this.formPaymentMethods.push(
          this.createPaymentMethodRow(
            transaction.id,
            transaction.transactionDate,
            transaction.createdById,
            paymentMethod,
            transaction.amount,
            transaction.emvData ? true : false,
            false,
            transaction.merchantDeviceId ? transaction.merchantDeviceId : 0
          )
        );
        if (transaction.paymentMethodId == this.creditsPaymentMethod.id) {
          this.creditFieldExists = true;
        }
      });
    }

    this.outstandingAmount = this.invoice.id !== 0 ? outstandingAmount : this.invoice.totalAmount;

    // Take into account Admin Fee
    if (this.adminFee && !this.isAdminFeePaid) this.outstandingAmount = this.outstandingAmount - this.adminFee;
    this.resetAdminFeeControls();
    if (this.outstandingAmount === 0 && !this.invoice.isPaid) {
      this.showCloseInvoiceButton = true;
    } else {
      this.showCloseInvoiceButton = false;
      if (this.outstandingAmount < 0) {
        this.paymentType = PaymentType.OverPayment;
        this.outstandingAmount = this.outstandingAmount * -1;
      }
    }

    this.initialOutstandingAmount = this.outstandingAmount;
    this.loading = false;
  }

  private setModalPaymentType() {
    if (!this.invoice) return;
    if (this.invoice.invoiceTypeId === InvoiceType.Refund) {
      if (this.invoice.invoiceLineItems.some((i) => i.clinicProductId)) {
        this.paymentType = PaymentType.RefundProduct;
      } else if (this.invoice.invoiceLineItems.some((i) => i.serviceId)) {
        this.paymentType = PaymentType.RefundService;
      } else if (this.invoice.invoiceLineItems.some((i) => i.serviceTypeDescription === 'Cancellation charge')) {
        this.paymentType = PaymentType.RefundPaidCancellation;
      } else if (this.invoice.invoiceLineItems.some((i) => i.isCredit)) {
        this.paymentType = PaymentType.RefundCredit;
      } else {
        this.paymentType = PaymentType.RefundAdminFee;
      }
    } else if (this.invoice.invoiceTypeId === InvoiceType.Recurring) {
      this.paymentType = PaymentType.Recurring;
    } else {
      this.paymentType = PaymentType.Regular;
    }
  }

  resetAdminFeeControls() {
    this.paymentForm.controls['adminFee'].enable();
    this.paymentForm.get('addBackToInventory').setValue(false, { emitEvent: false });
    this.adminFee = 0;
    this.isAdminFeePaid = false;
  }

  async saveEditedTransactions() {
    const invoicePayment: InvoicePayment = await this.createInvoicePayment(this.invoice.id, this.invoice.invoiceTypeId);
    this.loading = true;

    this.invoiceService.editTransactionsBulk(this.invoice.id, invoicePayment.transactions).subscribe(
      (transactions) => {
        this.stateOfCredits = 0;
        this.invoice.invoiceTransactions = transactions;
        this.invoiceService.invoiceUpdated.next(this.invoice.id);
        if (this.outstandingAmount === 0) {
          this.paymentComplete.next();
          this.financeService.invoicePaid.next();
        } else {
          this.onExit();
        }

        this.resetAdminFeeControls();
        this.resetModal();
        this.appointmentService.updateAllAppointmentsOnSchedule().subscribe();
        this.loading = false;
      },
      (errorResponse: HttpErrorResponse) => {
        this.errors = errorResponse.error.errors;
        this.loading = false;
      }
    );
  }

  private getInvoiceLineItemTaxes(invoice: Invoice) {
    this.loading = true;
    const { invoiceLineItemTaxKeys, invoiceLineItemTaxValuesObject } = this.invoicesService.getInvoiceLineItemTaxes(
      invoice,
      this.clinic
    );
    this.invoiceLineItemTaxKeys = invoiceLineItemTaxKeys;
    this.invoiceLineItemTaxValuesObject = invoiceLineItemTaxValuesObject;
    this.loading = false;
  }

  getPaymentMethods() {
    this.loading = true;
    this.paymentMethodsService
      .getPaymentMethods()
      .pipe(takeUntil(this.unsub))
      .subscribe((paymentMethods: PaymentMethod[]) => {
        this.squarePaymentMethod = paymentMethods.find((p) => p.id == PaymentMethodEnum.Square);
        this.paymentMethods = paymentMethods.filter((element) => element.isUserVisible === true);
        this.creditsPaymentMethod = this.paymentMethods?.find((p) => p.id === PaymentMethodEnum.Credits);
        this.giftCardPaymentMethod = this.paymentMethods?.find((p) => p.id === PaymentMethodEnum.GiftCard);
        this.formPaymentMethods = this.paymentForm.get('formPaymentMethods') as FormArray;

        // Check if using merchant devices
        if (this.clinic?.isUsingMerchantDevices) {
          this.merchantDeviceService
            .getMerchantDevicePaymentMethods()
            .pipe(takeUntil(this.unsub))
            .subscribe((merchantDevicePaymentMethods: PaymentMethod[]) => {
              this.merchantDevicePaymentMethods = merchantDevicePaymentMethods;
              // Filter out merchant device payment methods
              if (this.merchantDevicePaymentMethods.length > 0) {
                this.deviceCapablePaymentMethods = this.paymentMethods.filter(
                  (element) => element.isMerchantDeviceCapable
                );
                this.paymentMethods = this.paymentMethods.filter((element) => !element.isMerchantDeviceCapable);
                this.addPaymentMethodRow();
                // Signal that payment methods are ready
                this.paymentMethodsLoaded$.next();
                this.paymentMethodsLoaded$.complete();
              }
              this.loading = false;
            });
        } else {
          this.addPaymentMethodRow();
          // Signal that payment methods are ready
          this.paymentMethodsLoaded$.next();
          this.paymentMethodsLoaded$.complete();
        }
        this.loading = false;
      });
  }

  getPatientAvailableCredit() {
    this.financeService
      .getPatientAvailableCredit(this.patientId)
      .pipe(takeUntil(this.unsub))
      .subscribe((patientAvailableCredit: number) => {
        this.patientAvailableCredit = patientAvailableCredit;
      });
  }

  getPatientAvailableLoyaltyPoints() {
    this.financeService
      .getPatientAvailableLoyaltyPoints(this.patientId)
      .pipe(takeUntil(this.unsub))
      .subscribe((value: number) => {
        this.patientAvailableLoyaltyPoints.value = value;
      });
  }

  getPatientAvailableLoyaltyPointsDollarValue() {
    this.financeService
      .getPatientAvailableLoyaltyPointsDollarValue(this.patientId)
      .pipe(takeUntil(this.unsub))
      .subscribe((value: number) => {
        this.patientAvailableLoyaltyPoints.dollarValue = value;
      });
  }

  private getInvoicePaidAmount(invoiceId: number) {
    this.financeService
      .getInvoicePaidAmount(invoiceId)
      .pipe(takeUntil(this.unsub))
      .subscribe((paidAmount: number) => (this.paidAmount = paidAmount));
  }

  async onChargePayment(formControlIndex?: number) {
    this.loading = true;

    const invoiceType: InvoiceType =
      this.paymentType === PaymentType.Regular
        ? InvoiceType.Regular
        : this.paymentType === PaymentType.Recurring
        ? InvoiceType.Recurring
        : InvoiceType.Refund;

    const transactionType: FinanceTransactionType =
      this.paymentType === PaymentType.Regular || this.paymentType === PaymentType.Recurring
        ? FinanceTransactionType.Payment
        : FinanceTransactionType.Refund;

    let response: ResultModel<InvoiceTransaction>;

    try {
      // Pay new invoice
      if (this.invoice.id === 0) {
        const blankInvoice = {
          patientId: Number(this.patientId),
          invoiceDate: new Date(),
          invoiceTypeId: invoiceType,
        };
        const result = await this.invoicesService
          .addInvoice(blankInvoice, this.paymentType != PaymentType.Regular)
          .toPromise();
        const invoice = result.model;
        this.invoice.id = invoice.id;

        // Update line items with new invoice id
        this.invoice.invoiceLineItems.forEach((item) => {
          item.invoiceId = invoice.id;
        });
        await this.invoicesService.updateInvoiceLineItems(this.invoice.invoiceLineItems).toPromise();
      }

      let paymentRowControls: FormGroup;
      let refundTransaction: InvoiceTransaction;

      if (formControlIndex != null) {
        paymentRowControls = this.paymentForm.controls.formPaymentMethods['controls'][formControlIndex];
        const invoiceTransaction: InvoiceTransaction = this.createInvoiceTransactionFromControls(
          paymentRowControls,
          this.invoice.id,
          transactionType,
          true
        );

        // Update product line items if adding back to inventory. This needs to happen before the transaction is added and only if it is a full payment.
        if (this.paymentForm.get('addBackToInventory').value && this.outstandingAmount === 0) {
          let addBackToInventoryLineItems = this.invoice.invoiceLineItems.filter((i) => i.addBackToInventory);
          await this.invoicesService.updateInvoiceLineItems(addBackToInventoryLineItems).toPromise();
        }

        response = await this.financeService.addTransaction(invoiceTransaction).toPromise();

        if (response?.errors) {
          this.errors = [];
          this.warnings = [];
          for (let i = 0; i < response.errors.length; i++) {
            this.errors.push(response.errors[i]);
          }

          // If there are errors, revert the product line items addBackToInventory status
          let addBackToInventoryLineItems = this.invoice.invoiceLineItems.filter((i) => i.addBackToInventory);
          if (addBackToInventoryLineItems.length > 0) {
            addBackToInventoryLineItems.forEach((i) => (i.addBackToInventory = false));
            await this.invoicesService.updateInvoiceLineItems(addBackToInventoryLineItems).toPromise();
          }

          this.loading = false;
          return;
        } else if (response?.warnings) {
          this.errors = [];
          this.warnings = [];
          for (let i = 0; i < response.warnings.length; i++) {
            this.warnings.push(response.warnings[i]);
          }
        }

        // Check if it was a partial payment
        if (response.model.amount < invoiceTransaction.amount) {
          this.outstandingAmount = this.outstandingAmount + Number(invoiceTransaction.amount) - response.model.amount;
          if (paymentRowControls) {
            paymentRowControls.get('amount').setValue(response.model.amount, { emitEvent: false });
          }
        }

        // Refund credit
        if (this.paymentType === PaymentType.RefundCredit) {
          const transactionsTotal = response.model.amount;
          await this.financeService
            .updatePatientAvailableCredit(this.patientId, this.patientAvailableCredit - transactionsTotal)
            .toPromise();
          this.invoiceService.invoicesListUpdated.next();
        }
      } else {
        // Refund paid cancellation
        if (this.paymentType === PaymentType.RefundPaidCancellation) {
          let paidCancellationInvoiceTransaction = new PaidCancellationInvoiceTransaction();
          if (this.refundSquareCard) {
            refundTransaction = this.createInvoiceTransaction(
              this.invoice.id,
              transactionType,
              this.outstandingAmount,
              this.squarePaymentMethod.id
            );
            paidCancellationInvoiceTransaction.financeTransaction = refundTransaction;
          }
          if (this.refundConvergeCard) {
            refundTransaction = this.createInvoiceTransaction(
              this.invoice.id,
              transactionType,
              this.outstandingAmount,
              this.refundConvergeFinanceTransaction?.paymentMethodId
            );
            refundTransaction.convergePaymentId = this.refundConvergeFinanceTransaction?.convergePaymentId;
            paidCancellationInvoiceTransaction.financeTransaction = refundTransaction;
          }
          paidCancellationInvoiceTransaction.paidCancellation = this.paidCancellation;
          response = await this.financeService
            .addPaidCancellationRefundTransaction(paidCancellationInvoiceTransaction)
            .toPromise();

          if (response?.errors) {
            this.errors = [];
            this.warnings = [];
            for (let i = 0; i < response.errors.length; i++) {
              this.errors.push(response.errors[i]);
            }
            this.loading = false;
            return;
          } else if (response?.warnings) {
            this.errors = [];
            this.warnings = [];
            for (let i = 0; i < response.warnings.length; i++) {
              this.warnings.push(response.warnings[i]);
            }
          }
        }

        // Recurring payment
        if (this.paymentType === PaymentType.Recurring) {
          if (this.selectedSquareCard) {
            const squareTransaction = this.createInvoiceTransaction(
              this.invoice.id,
              transactionType,
              this.outstandingAmount,
              this.squarePaymentMethod.id
            );
            let squarePayment = this.createSquarePayment(this.outstandingAmount, this.selectedSquareCard.id);
            squareTransaction.squarePaymentId = 0;
            squareTransaction.squarePayment = squarePayment;

            response = await this.financeService.addTransaction(squareTransaction).toPromise();
            if (response?.errors) {
              this.errors = [];
              this.warnings = [];
              for (let i = 0; i < response.errors.length; i++) {
                this.errors.push(response.errors[i]);
              }
              this.loading = false;
              return;
            } else if (response?.warnings) {
              this.errors = [];
              this.warnings = [];
              for (let i = 0; i < response.warnings.length; i++) {
                this.warnings.push(response.warnings[i]);
              }
            }
          }
          // Converge
          if (this.selectedConvergeCard) {
            const convergeTransaction = this.createInvoiceTransaction(
              this.invoice.id,
              transactionType,
              this.outstandingAmount,
              29 // Other payment method
            );
            let convergePayment = this.createConvergePayment(
              this.outstandingAmount,
              this.selectedConvergeCard.convergeTokenId
            );
            convergeTransaction.convergePaymentId = 0;
            convergeTransaction.convergePayment = convergePayment;

            response = await this.financeService.addTransaction(convergeTransaction).toPromise();
            if (response?.errors) {
              this.errors = [];
              this.warnings = [];
              for (let i = 0; i < response.errors.length; i++) {
                this.errors.push(response.errors[i]);
              }
              this.loading = false;
              return;
            } else if (response?.warnings) {
              this.errors = [];
              this.warnings = [];
              for (let i = 0; i < response.warnings.length; i++) {
                this.warnings.push(response.warnings[i]);
              }
            }
          }
        }
      }

      if (paymentRowControls) {
        paymentRowControls.get('isCharged').setValue(true, { emitEvent: false });
        paymentRowControls.get('isManualEntry').disable();
      }

      if (this.paymentType === PaymentType.RefundPaidCancellation) {
        this.refundCharged = true;
      }

      if (this.paymentType === PaymentType.Recurring) {
        this.membershipCharged = true;
      }

      if (
        (this.outstandingAmount == 0 &&
          ((this.adminFee > 0 && this.isAdminFeePaid) || (this.adminFee == 0 && !this.isAdminFeePaid))) ||
        (this.paymentType === PaymentType.RefundPaidCancellation &&
          refundTransaction.amount == this.outstandingAmount) ||
        (this.paymentType === PaymentType.Recurring && (this.selectedSquareCard || this.selectedConvergeCard))
      ) {
        this.invoicePaidStatusUpdated();
      } else {
        // We still need to pay additional, so add a new payment row
        this.addPaymentMethodRow();
      }

      this.loading = false;
    } catch (error) {
      this.loading = false;
      if (error instanceof HttpErrorResponse) {
        if (error.error?.errors) this.errors = error.error.errors;
        else if (error.error) this.errors = error.error;
        else this.errors.push(error.message);
        this.resetInvoice();
      } else {
        throw error;
      }
    }
  }

  async onChargeAdminFee() {
    this.loading = true;

    const invoiceType: InvoiceType =
      this.paymentType === PaymentType.Regular
        ? InvoiceType.Regular
        : this.paymentType === PaymentType.Recurring
        ? InvoiceType.Recurring
        : InvoiceType.Refund;

    try {
      // Pay new invoice
      if (this.invoice.id === 0) {
        const blankInvoice = {
          patientId: Number(this.patientId),
          invoiceDate: new Date(),
          invoiceTypeId: invoiceType,
        };
        const result = await this.invoicesService.addInvoice(blankInvoice, true).toPromise();
        const invoice = result.model;
        this.invoice.id = invoice.id;
      }

      // Append admin fee line item if applicable
      if (this.adminFee != 0) {
        const adminFeeLineItem: InvoiceLineItem = this.createAdminFeeLineItem(this.invoice.id, this.adminFee * -1);
        //this.invoice.invoiceLineItems.push(adminFeeLineItem);
        await this.invoicesService.updateInvoiceLineItems([adminFeeLineItem]).toPromise();
      }

      // Update patients available credit if credit returned
      if (this.paymentType === PaymentType.RefundCredit) {
        await this.financeService
          .updatePatientAvailableCredit(this.patientId, this.patientAvailableCredit - this.adminFee)
          .toPromise();
        this.invoiceService.invoicesListUpdated.next();
      }

      this.isAdminFeePaid = true;
      this.paymentForm.controls['adminFee'].disable();

      if (this.outstandingAmount == 0 && (this.isAllPaymentsPaid() || this.isAllPaymentAmountsEmpty()))
        this.invoicePaidStatusUpdated();
      this.loading = false;
    } catch (error) {
      this.loading = false;
      if (error instanceof HttpErrorResponse) {
        if (error.error?.errors) this.errors = error.error.errors;
        else if (error.error) this.errors = error.error;
        else this.errors.push(error.message);
        this.resetInvoice();
      } else {
        throw error;
      }
    }
  } // Not used any longer, because we are doing single payment at a time

  private resetModal() {
    this.paymentForm.reset();
    this.errors = [];
    this.squareCardComponent?.ngOnInit();
    this.selectedSquareCard = null;
    this.creditFieldExists = false;
    this.stateOfCredits = 0;
    this.appointmentService.updateAllAppointmentsOnSchedule().subscribe();
    this.refundCharged = false;
    this.membershipCharged = false;
    this.refundConvergeFinanceTransaction = null;
    this.refundConvergeCard = null;
    this.refundSquareCard = null;

    if (this.formPaymentMethods) {
      for (let i = 0; i < this.formPaymentMethods.length; i++) {
        this.formPaymentMethods.removeAt(i);
      }
    }
  }

  private resetInvoice() {
    if (this.invoice.id !== 0) {
      this.getInvoicePaidAmount(this.invoice.id);
      this.getInvoiceDetails();
      this.getPatientAvailableCredit();
      this.getPatientAvailableLoyaltyPoints();
      this.getPatientAvailableLoyaltyPointsDollarValue();
    }
  }

  private async processTransactions(invoicePayment: InvoicePayment) {
    // Get Merchant Device Transactions and process them one at a time
    let merchantDeviceTransactions = invoicePayment.transactions.filter((t) => t.isMerchantDeviceTransaction);
    let errorExists = false;
    for (let i = 0; i < merchantDeviceTransactions.length; ) {
      let merchantDeviceTransaction = merchantDeviceTransactions[i];
      var response = await this.financeService.addTransaction(merchantDeviceTransaction).toPromise();
      if (response.errors) {
        errorExists = true;
        var shouldRetry = await this.displayTransactionError(response.errors).toPromise();
        if (!shouldRetry) {
          // Break out of other transactions if not retrying
          break;
        }
      } else {
        // Sleep for 1 second so card can be changed, etc.
        await new Promise((f) => setTimeout(f, 1000));
        i++;
      }
    }
    // Process non-merchant device transactions in a batch
    let otherTransactions = invoicePayment.transactions.filter((t) => !t.isMerchantDeviceTransaction);

    if (otherTransactions.length > 0) {
      invoicePayment.transactions = otherTransactions;
      await this.financeService.addTransactions(invoicePayment).toPromise();
    }

    return errorExists;
  }

  private displayTransactionError(errors: any[]): Observable<boolean> {
    var errorMsg = '';
    for (let i = 0; i < errors.length; i++) {
      errorMsg += errors[i].fieldErrors[0] + '<br>';
    }

    const dialogRef = this.dialog.open(GenericDialogComponent, {
      width: '500px',
      data: {
        title: 'Warning: There was an error processing a transaction.',
        content: errorMsg + '<br><strong>Would you like to retry?</strong>',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        showCancel: true,
      },
    });

    return dialogRef.afterClosed().pipe(
      takeUntil(this.unsub),
      map((result) => {
        if (result === 'confirm') {
          return true;
        }
        return false;
      })
    );
  }

  private createAdminFeeLineItem(invoiceId: number, amount: number): InvoiceLineItem {
    return {
      id: 0,
      invoiceId: invoiceId,
      note: null,
      unitPrice: amount,
      quantity: 1,
      total: amount,
      serviceType: null,
      serviceTypeDescription: 'Admin Fee',
      clinicProductId: null,
      relatedInvoiceLineItemId:
        this.invoice && this.invoice.invoiceLineItems && this.invoice.invoiceLineItems.length > 0
          ? this.invoice.invoiceLineItems[0].relatedInvoiceLineItemId
          : 0,
      isManual: true,
      isDeleted: false,
      finalTotal: amount,
      isCredit: false,
      serviceTemplateId: this.invoiceService.adminFeeServiceTemplate.id,
      addBackToInventory: false,
      invoiceLineItemTaxes: [],
      attributedToUserId: this.usersService.loggedInUser.id,
      attributedToUser: this.usersService.loggedInUser,
      plannedTreatmentMultipleId: null,
      isRecommendedProduct: false,
    };
  }

  private async createInvoicePayment(
    invoiceId: number,
    financeTransactionType: FinanceTransactionType,
    addUserAsCreatedBy: boolean = false
  ): Promise<InvoicePayment> {
    const transactions: InvoiceTransaction[] = [];
    (this.paymentForm.get('formPaymentMethods') as FormArray)['controls'].forEach((item: FormGroup) => {
      // We won't add the Merchant Device Transactions because they cannot be changed anyway
      if (!item.value.isMerchantDeviceTransaction)
        transactions.push(
          this.createInvoiceTransactionFromControls(item, invoiceId, financeTransactionType, addUserAsCreatedBy)
        );
    });

    if (this.paymentType === PaymentType.RefundPaidCancellation) {
      const squareTransaction = this.createInvoiceTransaction(
        invoiceId,
        financeTransactionType,
        this.outstandingAmount,
        this.squarePaymentMethod.id
      );
      transactions.push(squareTransaction);
    }
    if (this.paymentType === PaymentType.Recurring && this.selectedSquareCard) {
      const squareTransaction = this.createInvoiceTransaction(
        invoiceId,
        financeTransactionType,
        this.outstandingAmount,
        this.squarePaymentMethod.id
      );
      let squarePayment = this.createSquarePayment(this.outstandingAmount, this.selectedSquareCard.id);
      squareTransaction.squarePaymentId = 0;
      squareTransaction.squarePayment = squarePayment;
      transactions.push(squareTransaction);
    }

    let transactionsTotal = transactions.reduce((sum: number, cur: { amount: any }) => sum + Number(cur.amount), 0);
    // http://community.appsmith.com/solution/sum-values-reduce
    transactionsTotal = Number(transactionsTotal.toFixed(2));

    this.paymentType === PaymentType.Regular || this.paymentType === PaymentType.Recurring
      ? (transactionsTotal -= this.adminFee)
      : (transactionsTotal += this.adminFee);

    if (transactionsTotal < this.initialOutstandingAmount) {
      throw new Error(
        'The total of the payment cannot be less than the total of the invoice. Partial payments are not permitted.'
      );
    }

    return {
      patientId: this.patientId,
      transactions: transactions,
      paidCancellation: this.paidCancellation,
    };
  }

  private createInvoiceTransactionFromControls(
    formGroup: FormGroup,
    invoiceId: number,
    financeTransactionType: FinanceTransactionType,
    addUserAsCreatedBy: boolean
  ): InvoiceTransaction {
    let paymentMethod = null;
    if (formGroup.value.isMerchantDeviceTransaction)
      if (this.editTransactionMode)
        paymentMethod = this.deviceCapablePaymentMethods.find((p) => p.id == formGroup.value.method.id);
      else paymentMethod = this.merchantDevicePaymentMethods.find((p) => p.id == formGroup.value.method.id);
    else paymentMethod = this.paymentMethods.find((p) => p.id == formGroup.value.method.id);

    const isManualEntry = formGroup.value.isManualEntry ? formGroup.value.isManualEntry : false;
    const giftCard: GiftCard = formGroup.get('giftCard')?.value;

    return {
      id: formGroup.value.id ? formGroup.value.id : 0,
      invoiceId: invoiceId,
      transactionDate: formGroup.value.transactionDate ? formGroup.value.transactionDate : new Date(),
      financeTransactionType: null,
      patientId: this.patientId,
      financeTransactionTypeId: financeTransactionType,
      amount: formGroup.value.amount,
      paymentMethodId: formGroup.value.method.id,
      paymentMethod: null,
      isMerchantDeviceTransaction: paymentMethod.isMerchantDeviceTransaction ?? false,
      merchantDeviceId: paymentMethod.merchantDeviceId ?? 0,
      description: '',
      createdBy: null,
      createdById: addUserAsCreatedBy
        ? this.usersService.loggedInUser.id
        : formGroup.value.createdById
        ? formGroup.value.createdById
        : this.usersService.loggedInUser.id,
      emvData: null,
      isManualEntry: isManualEntry ?? false,
      txnData: null,
      giftCardId: giftCard && paymentMethod.id === this.giftCardPaymentMethod.id ? giftCard.id : null,
    };
  }

  private createInvoiceTransaction(
    invoiceId: number,
    financeTransactionTypeId: number,
    amount: number,
    paymentMethodId: number
  ): InvoiceTransaction {
    return {
      id: 0,
      invoiceId: invoiceId,
      transactionDate: new Date(),
      financeTransactionType: null,
      patientId: this.patientId,
      financeTransactionTypeId: financeTransactionTypeId,
      amount: amount,
      paymentMethodId: paymentMethodId,
      paymentMethod: null,
      isMerchantDeviceTransaction: false,
      merchantDeviceId: 0,
      description: '',
      createdBy: null,
      createdById: this.usersService.loggedInUser.id,
      emvData: null,
      isManualEntry: false,
      txnData: null,
    };
  }

  private createSquarePayment(amount: number, squareCardId: string): SquarePayment {
    return {
      id: 0,
      type: SquarePaymentType.Payment,
      status: SquarePaymentStatus.Reserved,
      squareCardId: squareCardId,
      amount: amount,
      clinicId: this.clinic.clinicId,
    };
  }

  private createConvergePayment(amount: number, patientTokenId: number): ConvergePayment {
    return {
      id: 0,
      type: ConvergePaymentType.Payment,
      status: ConvergePaymentStatus.Reserved,
      patientTokenId: patientTokenId,
      amount: amount,
    };
  }

  backToReturn() {
    this.backToReturnPanel.next();
  }

  async onExit() {
    this.paymentComplete.next();
    this.paymentForm.reset();
  }

  async onPrintInvoice() {
    this.loading = true;

    const componentRef = await this.createStandardInvoiceComponent();
    setTimeout(async () => {
      await componentRef.instance.exportToPDF();
      componentRef.destroy();
      this.exportTemplate.clear();
      this.loading = false;
    }, 150);
  }

  async onEmailInvoice(email: string) {
    const componentRef = await this.createStandardInvoiceComponent();
    await componentRef.instance.emailInvoice(email);
    componentRef.destroy();
    this.exportTemplate.clear();
    return true;
  }

  getEmailInvoiceObservable = (email: string) => {
    return defer(() => this.onEmailInvoice(email));
  };

  private async createStandardInvoiceComponent() {
    const componentRef = this.exportTemplate.createComponent(StandardInvoiceComponent);
    const invoiceComponent = componentRef.instance as StandardInvoiceComponent;
    invoiceComponent.externalInvoiceId = this.invoice.id;
    invoiceComponent.clinic = this.clinic;
    invoiceComponent.invoice = this.invoice;
    await invoiceComponent.invoiceReadyForPrint.pipe(first((ready) => ready == true)).toPromise();
    return componentRef;
  }

  async closeInvoiceHandler() {
    this.loading = true;
    this.updateInvoicePaidStatus();
    this.loading = false;
    this.onExit();
  }

  updateInvoicePaidStatus() {
    this.loading = true;
    this.invoicesService
      .updateInvoicePaidStatus(this.invoice.id, true)
      .pipe(takeUntil(this.unsub))
      .subscribe(
        () => {
          this.invoicePaidStatusUpdated();
          this.loading = false;
        },
        (errorResponse: HttpErrorResponse) => {
          this.errors = errorResponse.error.errors;
          this.loading = false;
        }
      );
  }

  invoicePaidStatusUpdated() {
    this.invoicesService.invoiceUpdated.next(this.invoice.id);
    this.invoicesService.invoicesListUpdated.next();
    this.financeService.invoicePaid.next();
    this.invoice.isPaid = true;
  }

  doneButtonsDisabled(): boolean {
    if (this.paymentType === PaymentType.Recurring && this.membershipCharged) return false;
    if (
      this.paymentType !== PaymentType.RefundPaidCancellation &&
      this.formPaymentMethods?.length === 0 &&
      this.adminFee > 0 &&
      !this.isAdminFeePaid
    )
      return true;
    if (this.paymentType === PaymentType.RefundPaidCancellation && this.refundCharged) return false;
    if (!this.paymentForm.valid) return true;
    if (this.patientAvailableCredit - this.stateOfCredits < 0) return true;
    if (this.isAllPaymentsPaid() && this.outstandingAmount === 0 && this.adminFee > 0 && this.isAdminFeePaid)
      return false;

    if (this.outstandingAmount !== 0) return true;
    if (!this.isAllPaymentsPaid()) return true;
    if (this.adminFee > 0 && !this.isAdminFeePaid) return true;
    return false;
  }

  checkInvalidPaymentAmount(): boolean {
    const isOutstandingAmountNegative = this.outstandingAmount < 0;
    const isPatientCreditNegative = this.patientAvailableCredit < 0;
    return isOutstandingAmountNegative || isPatientCreditNegative;
  }

  isGiftCardMethod(rowGroup: FormGroup): boolean {
    return rowGroup.get('method').value && rowGroup.get('method').value?.id === this.giftCardPaymentMethod?.id;
  }

  isSquarePayButtonDisabled(): boolean {
    if (this.membershipCharged) return true;

    if (this.paymentType === PaymentType.Recurring) {
      if (this.selectedConvergeCard == null && this.selectedSquareCard == null) return true;
      else return false;
    }

    if (this.refundCharged) return true;

    if (
      this.paymentType === PaymentType.RefundPaidCancellation &&
      this.refundSquareCard == null &&
      this.refundConvergeCard == null
    )
      return true;

    return false;
  }

  isAddNewPaymentRowDisabled() {
    // If we are editing transactions then it should not be disabled
    if (this.editTransactionMode) return false;

    var controlsArray = this.paymentForm.controls.formPaymentMethods['controls'];

    if (controlsArray.length == 0) return false;
    if (!this.paymentForm.valid) return true;
    // If any row is uncharged, its disabled
    for (let i = 0; i < controlsArray.length; i++) {
      var isCharged = controlsArray[i].value.isCharged;
      if (!isCharged) return true;
    }
    return this.outstandingAmount === 0;
  }

  isAddNewPaymentRowHidden() {
    // If we are editing transactions it should not be hidden
    if (this.editTransactionMode) return false;

    return true;
  }

  isAddBalanceButtonDisabled() {
    // If we are editing transactions then it should not be disabled
    if (this.editTransactionMode) return false;

    var controlsArray = this.paymentForm.controls.formPaymentMethods['controls'];

    if (controlsArray.length == 0) return false;

    // If any row is uncharged with an amount, its disabled
    for (let i = 0; i < controlsArray.length; i++) {
      var amount = controlsArray[i].value.amount;
      var isCharged = controlsArray[i].value.isCharged;
      if (
        (isCharged === null || isCharged === undefined || !isCharged) &&
        amount !== null &&
        amount !== undefined &&
        amount !== ''
      )
        return true;
    }
    return this.outstandingAmount === 0;
  }

  isAllPaymentsPaid() {
    var controlsArray = this.paymentForm.controls.formPaymentMethods['controls'];

    if (controlsArray.length == 0) return false;

    for (let i = 0; i < controlsArray.length; i++) {
      var isCharged = controlsArray[i].value.isCharged;
      if (!isCharged) return false;
    }

    return true;
  }

  isAllPaymentAmountsEmpty() {
    var controlsArray = this.paymentForm.controls.formPaymentMethods['controls'];

    if (controlsArray.length == 0) return true;

    for (let i = 0; i < controlsArray.length; i++) {
      var amount = controlsArray[i].value.amount;
      if (amount != '' && amount == '0') return false;
    }

    return true;
  }

  isAdminFeePayButtonDisabled() {
    if (this.adminFee <= 0) return true;
    if (this.isAdminFeePaid) return true;
    if (this.outstandingAmount < 0) return true;
    return false;
  }

  onSquareCardSelected(card: SquareCard) {
    this.selectedSquareCard = card;
  }

  onConvergeCardSelected(card: ConvergeCard) {
    this.selectedConvergeCard = card;
  }

  ngOnDestroy() {
    this.unsub.next();
    this.unsub.complete();
  }
}
